<script setup>
import { computed, onMounted, ref } from 'vue';
import { useStore } from 'dashboard/composables/store';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import InboxesAPI from 'dashboard/api/inboxes';
import DeleteModal from 'dashboard/components/widgets/modal/DeleteModal.vue';
import WhatsAppTemplateHeader from './components/WhatsAppTemplateHeader.vue';
import WhatsAppTemplateSkeleton from './components/WhatsAppTemplateSkeleton.vue';
import WhatsAppTemplateFilters from './components/WhatsAppTemplateFilters.vue';
import WhatsAppTemplateTable from './components/WhatsAppTemplateTable.vue';
import WhatsAppTemplateEmptyState from './components/WhatsAppTemplateEmptyState.vue';
import { deleteTemplateErrorMessage } from './utils/whatsappErrorHandler';

const store = useStore();
const router = useRouter();
const { t } = useI18n();

// ── Filter state ──────────────────────────────────────────────────────────────
const searchQuery = ref('');
const selectedStatuses = ref([]);
const selectedCategories = ref([]);
const selectedInboxIds = ref([]);
const isStatusOpen = ref(false);
const isCategoryOpen = ref(false);
const isInboxOpen = ref(false);

const statusOptions = [
  { id: 'APPROVED', label: 'Approved', icon: '✅' },
  { id: 'PENDING', label: 'Pending', icon: '⏳' },
  { id: 'REJECTED', label: 'Rejected', icon: '❌' },
];

const categoryOptions = [
  { id: 'MARKETING', label: 'Marketing', icon: '📢' },
  { id: 'UTILITY', label: 'Utility', icon: '🔧' },
  { id: 'AUTHENTICATION', label: 'Authentication', icon: '🔐' },
];

// ── Store derivations ─────────────────────────────────────────────────────────
const isFetching = computed(
  () => store.getters['inboxes/getUIFlags']?.isFetching || false
);

const OFFICIAL_API_PROVIDERS = ['whatsapp_cloud', 'whatsapp_coexistence'];

const whatsappInboxes = computed(() => {
  const inboxes = store.getters['inboxes/getInboxes'];
  if (!Array.isArray(inboxes)) return [];
  return inboxes.filter(
    inbox =>
      inbox.channel_type === 'Channel::Whatsapp' &&
      OFFICIAL_API_PROVIDERS.includes(inbox.provider)
  );
});

const alltemplates = computed(() => {
  if (!Array.isArray(whatsappInboxes.value)) return [];
  let result = [];
  whatsappInboxes.value.forEach(inbox => {
    const raw = store.getters['inboxes/getWhatsAppTemplates'](inbox.id);
    const templates = Array.isArray(raw) ? raw : [];
    result = [
      ...result,
      ...templates.map(tmpl => ({
        ...tmpl,
        inboxName: inbox.name || 'Unknown',
        inboxId: inbox.id,
        name: tmpl.name || '',
        category: tmpl.category || '',
        status: tmpl.status || '',
        language: tmpl.language || '',
      })),
    ];
  });
  return result;
});

const inboxOptions = computed(() =>
  whatsappInboxes.value.map(inbox => ({
    id: inbox.id,
    label: inbox.name || `Inbox #${inbox.id}`,
  }))
);

const approvedCount = computed(
  () =>
    alltemplates.value.filter(tmpl => tmpl.status?.toLowerCase() === 'approved')
      .length
);

const filteredTemplates = computed(() => {
  let result = alltemplates.value;
  const query = searchQuery.value.toLowerCase().trim();
  if (query) {
    result = result.filter(
      tmpl =>
        tmpl.name.toLowerCase().includes(query) ||
        tmpl.inboxName.toLowerCase().includes(query) ||
        tmpl.language.toLowerCase().includes(query)
    );
  }
  if (selectedStatuses.value.length > 0) {
    result = result.filter(tmpl =>
      selectedStatuses.value.includes(tmpl.status?.toUpperCase())
    );
  }
  if (selectedCategories.value.length > 0) {
    result = result.filter(tmpl =>
      selectedCategories.value.includes(tmpl.category?.toUpperCase())
    );
  }
  if (selectedInboxIds.value.length > 0) {
    result = result.filter(tmpl =>
      selectedInboxIds.value.includes(tmpl.inboxId)
    );
  }
  return result;
});

const hasOfficialApiInboxes = computed(() =>
  whatsappInboxes.value.some(inbox => {
    const provider = inbox.provider || 'default';
    return provider === 'default' || OFFICIAL_API_PROVIDERS.includes(provider);
  })
);

const isSyncDisabled = computed(() => !hasOfficialApiInboxes.value);

// ── Sync templates ────────────────────────────────────────────────────────────
const isSyncingTemplates = ref(false);

const syncTemplates = async () => {
  isSyncingTemplates.value = true;
  try {
    await Promise.all(
      whatsappInboxes.value.map(inbox =>
        store.dispatch('inboxes/syncTemplates', inbox.id)
      )
    );
    useAlert(t('CAMPAIGN.WHATSAPP_OFFICIAL.SYNC.SUCCESS'));
  } catch {
    useAlert(t('CAMPAIGN.WHATSAPP_OFFICIAL.SYNC.ERROR'));
  } finally {
    isSyncingTemplates.value = false;
  }
};

// ── Send broadcast ────────────────────────────────────────────────────────────
const handleSendBroadcast = async template => {
  const basicData = {
    template: JSON.parse(JSON.stringify(template)),
    inboxId: template.inboxId,
    isLoading: true,
  };
  await store.dispatch('campaigns/clearCampaignData');
  await store.dispatch('campaigns/saveCampaignData', basicData);
  router.push({
    name: 'campaigns_whatsapp_official_home_create',
    query: { inboxId: template.inboxId, templateName: template.name },
  });
  const messageLimit = await store.dispatch(
    'externalDatabaseConnection/fetchWhatsappMessageLimit',
    { inboxId: template.inboxId, refresh: true }
  );
  await store.dispatch('campaigns/saveCampaignData', {
    ...basicData,
    messageLimit: JSON.parse(JSON.stringify(messageLimit)),
    isLoading: false,
  });
};

// ── Delete template ───────────────────────────────────────────────────────────
const showDeleteModal = ref(false);
const templateToDelete = ref(null);
const isDeleting = ref(false);

const handleDelete = template => {
  templateToDelete.value = template;
  showDeleteModal.value = true;
};

const closeDeleteModal = () => {
  showDeleteModal.value = false;
  templateToDelete.value = null;
};

const confirmDelete = async () => {
  if (!templateToDelete.value || isDeleting.value) return;
  isDeleting.value = true;
  try {
    const template = templateToDelete.value;
    await InboxesAPI.deleteWhatsAppTemplate(template.inboxId, template.name);
    const inbox = store.getters['inboxes/getInbox'](template.inboxId);
    if (inbox) {
      store.commit('inboxes/EDIT_INBOXES', {
        ...inbox,
        message_templates: (inbox.message_templates || []).filter(
          tmpl => tmpl.name !== template.name
        ),
      });
    }
    useAlert(t('CAMPAIGN.WHATSAPP_OFFICIAL.DELETE.SUCCESS'));
    closeDeleteModal();
  } catch (error) {
    useAlert(deleteTemplateErrorMessage(error, t));
  } finally {
    isDeleting.value = false;
  }
};

// ── Navigation ────────────────────────────────────────────────────────────────
const openBuilder = () => {
  router.push({ name: 'campaigns_whatsapp_official_builder' });
};

const redirectToInboxSetup = () => {
  router.push({
    name: 'settings_inboxes_page_channel',
    params: { sub_page: 'whatsapp' },
    query: { provider: 'whatsapp' },
  });
};

onMounted(() => {
  store.dispatch('inboxes/get');
});
</script>

<template>
  <div class="flex flex-col h-full bg-n-background">
    <WhatsAppTemplateHeader
      :approved-count="approvedCount"
      :inbox-count="whatsappInboxes.length"
      :is-fetching="isFetching"
      :is-syncing-templates="isSyncingTemplates"
      :is-sync-disabled="isSyncDisabled"
      :has-inboxes="whatsappInboxes.length > 0"
      @sync-templates="syncTemplates"
      @open-builder="openBuilder"
    />

    <main class="flex flex-col flex-1 min-h-0 w-full mt-1">
      <!-- Skeleton loading -->
      <WhatsAppTemplateSkeleton v-if="isFetching" />

      <!-- Templates content -->
      <div
        v-else-if="alltemplates.length > 0"
        class="flex flex-col flex-1 min-h-0 gap-6 px-3 p-2 animate-[fadeIn_0.5s_ease-in-out]"
      >
        <WhatsAppTemplateFilters
          v-model:search-query="searchQuery"
          v-model:selected-statuses="selectedStatuses"
          v-model:selected-categories="selectedCategories"
          v-model:selected-inbox-ids="selectedInboxIds"
          v-model:is-status-open="isStatusOpen"
          v-model:is-category-open="isCategoryOpen"
          v-model:is-inbox-open="isInboxOpen"
          :status-options="statusOptions"
          :category-options="categoryOptions"
          :inbox-options="inboxOptions"
        />

        <WhatsAppTemplateTable
          :data="filteredTemplates"
          @send-broadcast="handleSendBroadcast"
          @delete="handleDelete"
        />
      </div>

      <!-- Empty state -->
      <WhatsAppTemplateEmptyState
        v-else
        :has-inboxes="whatsappInboxes.length > 0"
        :is-sync-disabled="isSyncDisabled"
        :is-syncing-templates="isSyncingTemplates"
        @redirect-to-setup="redirectToInboxSetup"
        @sync-templates="syncTemplates"
      />
    </main>

    <DeleteModal
      v-model:show="showDeleteModal"
      :title="t('CAMPAIGN.WHATSAPP_OFFICIAL.DELETE.TITLE')"
      :message="
        t('CAMPAIGN.WHATSAPP_OFFICIAL.DELETE.MESSAGE', {
          name: templateToDelete?.name,
        })
      "
      :confirm-text="t('CAMPAIGN.WHATSAPP_OFFICIAL.DELETE.CONFIRM')"
      :reject-text="t('CAMPAIGN.WHATSAPP_OFFICIAL.DELETE.CANCEL')"
      :is-loading="isDeleting"
      :on-close="closeDeleteModal"
      :on-confirm="confirmDelete"
    />
  </div>
</template>
